/**
 * Frontend Environment Variables Validation Utility
 * Ensures all required Vite environment variables are properly configured
 */

export interface FrontendEnvConfig {
  // Supabase Configuration
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  
  // API Configuration
  VITE_API_BASE_URL: string;
  
  // Optional configurations
  VITE_APP_ENV?: string;
  VITE_ENABLE_ANALYTICS?: string;
}

const requiredEnvVars: (keyof FrontendEnvConfig)[] = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY',
  'VITE_API_BASE_URL'
];

const optionalEnvVars: Record<string, string> = {
  VITE_APP_ENV: 'production',
  VITE_ENABLE_ANALYTICS: 'false'
};

/**
 * Validates all required frontend environment variables
 * @throws Error if any required environment variable is missing
 * @returns Validated environment configuration
 */
export function validateFrontendEnvironmentVariables(): FrontendEnvConfig {
  const missingVars: string[] = [];
  const config: Partial<FrontendEnvConfig> = {};
  
  // Check required variables
  for (const varName of requiredEnvVars) {
    const value = import.meta.env[varName];
    if (!value || value.trim() === '') {
      missingVars.push(varName);
    } else {
      (config as any)[varName] = value.trim();
    }
  }
  
  // Set optional variables with defaults
  for (const [varName, defaultValue] of Object.entries(optionalEnvVars)) {
    (config as any)[varName] = import.meta.env[varName] || defaultValue;
  }
  
  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}\n` +
      'Please ensure all required environment variables are set in your .env file.\n' +
      'Check .env.example for reference.'
    );
  }
  
  return config as FrontendEnvConfig;
}

/**
 * Validates environment variables and logs the result
 * @param context Optional context for logging
 * @returns Validated environment configuration
 */
export function validateAndLogFrontendEnvironment(context: string = 'frontend'): FrontendEnvConfig {
  try {

    const config = validateFrontendEnvironmentVariables();
    // Environment validation successful - no need to log in production
    return config;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`❌ Frontend environment validation failed for ${context}:`, errorMessage);
    throw error;
  }
}

/**
 * Checks if we're running in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

/**
 * Checks if we're running in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD;
}

/**
 * Gets Vite environment variable with fallback and validation
 */
export function getViteEnvVar(name: string, fallback?: string, required: boolean = false): string {
  const value = import.meta.env[name];
  
  if (!value || value.trim() === '') {
    if (required) {
      throw new Error(`Required environment variable ${name} is not set`);
    }
    if (fallback !== undefined) {
      return fallback;
    }
    throw new Error(`Environment variable ${name} is not set and no fallback provided`);
  }
  
  return value.trim();
}